import React, { useEffect, useRef, useState } from 'react'
import { DonutChart } from '@mantine/charts'
import { useNavigate } from 'react-router-dom'
import { modals } from '@mantine/modals'
import { motion } from 'framer-motion'
import { useAuthStore } from '../../store/useAuthStore'
import useRewardStore from '../../store/rewardStore'
import { apiRequest } from '../../utils/api'
import BaseButton from '../common/BaseButton'
import AvatarDisplay from './AvatarDisplay'
import subStyles from '../../styles/individual/SubscribeModal.module.scss'

const DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

// 이번 주 월~일 기준으로 lastActivityDate 기준 streak만큼 체크
const getWeekChecks = (streak, lastActivityDate) => {
  if (!lastActivityDate || streak === 0) return DAYS.map(() => false)

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dayOfWeek = today.getDay() // 0=일
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const monday = new Date(today)
  monday.setDate(today.getDate() - mondayOffset)

  const lastDate = new Date(lastActivityDate)
  lastDate.setHours(0, 0, 0, 0)

  return DAYS.map((_, i) => {
    const day = new Date(monday)
    day.setDate(monday.getDate() + i)
    if (day > today) return false
    // 이 날짜가 lastActivityDate 기준 streak 범위 안에 있으면 체크
    const diffFromLast = Math.round((lastDate - day) / 86400000)
    return diffFromLast >= 0 && diffFromLast < streak
  })
}

const shineKeyframe = `
  @keyframes shine {
    0% { left: -40%; }
    100% { left: 100%; }
  }
  .dash-grid-top { display: grid; grid-template-columns: minmax(0,1.6fr) minmax(0,1fr); gap: 20px; margin-bottom: 16px; align-items: stretch; }
  .dash-grid-mid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 16px; }
  .dash-grid-improve { display: grid; grid-template-columns: minmax(0,2.2fr) minmax(0,1fr); gap: 16px; margin-bottom: 16px; }
  .dash-donut-row { display: flex; align-items: center; justify-content: space-evenly; gap: 16px; padding: 4px 0; }

  @media (max-width: 1024px) {
    .dash-grid-top { grid-template-columns: 1fr; }
    .dash-grid-mid { grid-template-columns: 1fr 1fr; }
    .dash-grid-improve { grid-template-columns: 1fr; }
  }
  @media (max-width: 768px) {
    .dash-grid-mid { grid-template-columns: 1fr; }
    .dash-donut-row { flex-wrap: wrap; gap: 12px; }
  }
`

const IndividualDashboardBox = () => {
  const navigate  = useNavigate()
  const user      = useAuthStore((state) => state.user)

  // 기존 대시보드 데이터
  const [summaryData, setSummaryData] = useState({ averageAnswers: 0, level: '', progress: 0 })
  const [wrongData,   setWrongData]   = useState([])
  const [wrongPercent,setWrongPercent]= useState([])
  const [hoveredIdx,  setHoveredIdx]  = useState(null)

  // 보상 store
  const {
    coins, gems, dailyStreak, lastActivityDate,
    earnedBadges, totalBadgesEarned, avatar, nextRewardItem,
    fetchRewardSummary, fetchEarnedBadges, fetchNextReward,
  } = useRewardStore()

  // ── 기존 fetchStudentDashboard ──────────────────────────────
  // 실패 후 재시도 방지: 시도 여부를 ref로 추적
  const dashboardFetchAttempted = useRef(false)

  useEffect(() => {
    if (user?.ID && !dashboardFetchAttempted.current) {
      dashboardFetchAttempted.current = true
      fetchStudentDashboard(user.ID)
    }
  }, [user?.ID])

  const fetchStudentDashboard = async (id) => {
    try {
      const res = await apiRequest('/fetchStudentDashboard', 'POST', { ID: id })
      if (!res.data) return
      const d = res.data
      setSummaryData({ averageAnswers: d.AVG, level: d.LEVEL, progress: d.PROGRESS })

      const total = d.TOTAL?.total_problems || 0
      const wrong = d.WRONG || []
      const get = (attempt) => wrong.find((v) => v.attempt === attempt)?.value || 0

      setWrongPercent([
        { percent: total ? (((total - get(0)) / total) * 100).toFixed(1) : '0.0' },
        { percent: total ? (((total - get(1)) / total) * 100).toFixed(1) : '0.0' },
        { percent: total ? (((total - get(2)) / total) * 100).toFixed(1) : '0.0' },
        { percent: total ? (((total - get(3)) / total) * 100).toFixed(1) : '0.0' },
      ])
      setWrongData([
        [{ name: 'Correct', value: total - get(0), color: '#30ACC6' }, { name: 'Wrong (1st)', value: get(0), color: '#F1F1F1' }],
        [{ name: 'Correct', value: total - get(1), color: '#8F70AE' }, { name: 'Wrong (2nd)', value: get(1), color: '#F1F1F1' }],
        [{ name: 'Correct', value: total - get(2), color: '#F7B52C' }, { name: 'Wrong (3rd)', value: get(2), color: '#F1F1F1' }],
        [{ name: 'Correct', value: total - get(3), color: '#8DC21F' }, { name: 'Wrong (4th)', value: get(3), color: '#F1F1F1' }],
      ])
    } catch (_) {}
  }

  // ── 보상 데이터 로딩 ────────────────────────────────────────
  useEffect(() => {
    if (!user?.USER_ID) return
    fetchRewardSummary(user.USER_ID).catch(() => {})
    fetchEarnedBadges(user.USER_ID).catch(() => {})
  }, [user?.USER_ID])

  useEffect(() => {
    if (user?.USER_ID && coins !== undefined) {
      fetchNextReward(user.USER_ID, coins).catch(() => {})
    }
  }, [user?.USER_ID, coins])

  // ── XP 계산 (progress% × 15, max 1500) ─────────────────────
  const currentXP  = Math.round((summaryData.progress || 0) * 15)
  const maxXP      = 1500
  const xpPct      = Math.min((currentXP / maxXP) * 100, 100)
  const currentLevelNum = parseInt((summaryData.level || '').replace(/\D/g, ''), 10) || 1

  // ── 아바타 이미지 (user.AVATAR 필드 폴백) ──────────────────
  const userAvatarSrc = user?.AVATAR || null

  // ── 스트릭 요일 체크 ────────────────────────────────────────
  const weekChecks = getWeekChecks(dailyStreak, lastActivityDate)

  // ── Learning Path (레벨 1~12) ───────────────────────────────
  const totalLevels = 12
  const levels = Array.from({ length: totalLevels }, (_, i) => i + 1)

  // ── 네비게이션 ───────────────────────────────────────────────
  const handleStartLesson = () => {
    if (user?.SUB === 'Y') {
      navigate('/solver')
    } else {
      modals.open({
        children: (
          <motion.div
            className={subStyles.overlay}
            initial={{ opacity: 0, scale: 0.92, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <div className={subStyles.header}>
              <div className={subStyles.characterWrapper}>
                <img
                  src="/assets/rabbit-blue-monster-cry.png"
                  alt="Buddy"
                  className={subStyles.character}
                />
              </div>
              <div className={subStyles.headerBadge}>
                <span>✦ Unlock Full Access</span>
              </div>
            </div>

            <div className={subStyles.body}>
              <p className={subStyles.title}>Keep Learning!</p>
              <p className={subStyles.subtitle}>Subscribe to access all lessons and features.</p>

              <div className={subStyles.benefitList}>
                <div className={subStyles.benefitItem}>
                  <div className={subStyles.benefitIcon}>✓</div>
                  Unlimited lessons & quizzes
                </div>
                <div className={subStyles.benefitItem}>
                  <div className={subStyles.benefitIcon}>✓</div>
                  Personalized learning path
                </div>
                <div className={subStyles.benefitItem}>
                  <div className={subStyles.benefitIcon}>✓</div>
                  Progress tracking & reports
                </div>
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                <BaseButton
                  className={subStyles.subscribeBtn}
                  onClick={() => { modals.closeAll(); navigate('/subscribe') }}
                >
                  Subscribe Now →
                </BaseButton>
              </motion.div>

              <button className={subStyles.cancelLink} onClick={() => modals.closeAll()}>
                Maybe later
              </button>
            </div>
          </motion.div>
        ),
        centered: true,
        withCloseButton: false,
        padding: 0,
        radius: 'md',
      })
    }
  }

  const getProgressColor = (p) => {
    if (p < 30) return ['#FF6B6B', '#FFA07A']
    if (p < 70) return ['#4F6BED', '#69A7FF']
    return ['#22C55E', '#86EFAC']
  }

  const improvement =
    wrongPercent.length >= 4
      ? (Number(wrongPercent[3].percent || 0) - Number(wrongPercent[0].percent || 0)).toFixed(1)
      : '0.0'

  const chartLabels = ['First Try', 'Review 1', 'Review 2', 'Mastery']

  // ────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{shineKeyframe}</style>

      {/* ── 상단 2열 ─────────────────────────────────────────── */}
      <div className="dash-grid-top">

        {/* 캐릭터 + 레벨 카드 */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1 }}>
            {/* 아바타 (레이어: 배경 → 캐릭터 → 모자/아이템) */}
            <AvatarDisplay
              avatar={avatar}
              userAvatar={userAvatarSrc}
              size={230}
              onClick={() => navigate('/avatar-shop')}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, color: '#7A889B', fontWeight: 600 }}>Hi, {user?.NICKNAME || 'Student'}! 👋</p>
              <p style={{ margin: '4px 0 14px', fontSize: 36, fontWeight: 900, color: '#0D2A4F', lineHeight: 1.1 }}>
                {summaryData.level || 'Level —'}
              </p>

              {/* XP 바 */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#D97706', display: 'flex', alignItems: 'center', gap: 3 }}><img src="/assets/icons/lightning.png" alt="xp" style={{ width: 12, height: 12, objectFit: 'contain' }} />{currentXP.toLocaleString()} / {maxXP.toLocaleString()}</span>
                  <span style={{ fontSize: 11, color: '#7A889B' }}>{maxXP - currentXP} XP to next level</span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: '#F3EFE5', overflow: 'hidden' }}>
                  <div style={{ width: `${xpPct}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg,#F59E0B,#FCD34D)', transition: 'width .6s ease', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: '-40%', width: '40%', height: '100%', background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent)', animation: 'shine 2s infinite' }} />
                  </div>
                </div>
              </div>

              {/* Coins + Stars */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={statChip('#FFF8EC', '#F59E0B')}>
                  <img src="/assets/icons/coin.png" alt="coin" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  <div>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0D2A4F' }}>{coins.toLocaleString()}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#7A889B', fontWeight: 600 }}>Coins</p>
                  </div>
                </div>
                <div style={statChip('#F3EEFF', '#8F70AE')}>
                  <img src="/assets/icons/gem.png" alt="gem" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  <div>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0D2A4F' }}>{gems.toLocaleString()}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#7A889B', fontWeight: 600 }}>Gems</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Progress 카드 */}
        <div style={{ ...card, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Mountain scene — flex:1 so it fills the same height as the avatar card */}
          {(() => {
            const progress = summaryData.progress || 0
            const p = Math.min(progress / 100, 1)

            // Winding trail: zigzag waypoints in image-space (1725 × 912)
            // Each leg alternates direction (left → right → left) like real switchbacks
            const waypoints = [
              [1340, 830],   // base: right side of mountain
              [500, 648],   // swing left across slope
              [1200, 522],   // switchback right
              [895,  392],   // swing left again
              [965, 262],   // small right bump
              [885,  225],    // peak / flag
            ]

            // Cumulative arc lengths for uniform speed along path
            const segLens = waypoints.slice(1).map((wp, i) => {
              const dx = wp[0] - waypoints[i][0]
              const dy = wp[1] - waypoints[i][1]
              return Math.sqrt(dx * dx + dy * dy)
            })
            const cumLen = segLens.reduce((acc, l) => [...acc, acc[acc.length - 1] + l], [0])
            const totalLen = cumLen[cumLen.length - 1]

            const getPoint = (t) => {
              const target = t * totalLen
              for (let i = 0; i < segLens.length; i++) {
                if (target <= cumLen[i + 1] || i === segLens.length - 1) {
                  const segT = (target - cumLen[i]) / segLens[i]
                  return [
                    waypoints[i][0] + segT * (waypoints[i + 1][0] - waypoints[i][0]),
                    waypoints[i][1] + segT * (waypoints[i + 1][1] - waypoints[i][1]),
                  ]
                }
              }
              return waypoints[waypoints.length - 1]
            }

            const [charX, charY] = getPoint(p)
            const charXPct = (charX / 1725) * 100
            const charYPct = (charY / 912) * 100

            const motivationIcon =
              progress < 25 ? '/assets/icons/star.png' :
              progress < 50 ? '/assets/icons/strength.png' :
              progress < 75 ? '/assets/icons/streak.png' :
              progress < 100 ? '/assets/icons/trophy.png' : '/assets/icons/celebration.png'
            const motivationText =
              progress < 25 ? "Let's get started!" :
              progress < 50 ? 'Great progress!' :
              progress < 75 ? 'Keep climbing!' :
              progress < 100 ? 'Almost at the top!' : 'Summit reached!'

            const dots = Array.from({ length: 18 }, (_, i) => {
              const t = i / 17
              const [x, y] = getPoint(t)
              return { x, y, reached: t <= p }
            })

            return (
              <>
                {/* Image + overlays */}
                <div style={{ position: 'relative', flex: 1, minHeight: 160, overflow: 'hidden' }}>
                  <img
                    src="/assets/mountain-bg.png"
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />

                  {/* Bezier trail dots overlay */}
                  <svg
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                    viewBox="0 0 1725 912"
                    preserveAspectRatio="xMidYMid slice"
                  >
                    {dots.map((dot, i) => (
                      <circle key={i} cx={dot.x} cy={dot.y} r={dot.reached ? 14 : 11}
                        fill={dot.reached ? '#F59E0B' : 'rgba(255,255,255,0.55)'}
                        stroke={dot.reached ? '#D97706' : 'rgba(0,0,0,0.12)'}
                        strokeWidth="3"
                      />
                    ))}
                  </svg>

                  {/* Hiker character */}
                  <img
                    src="/assets/hiker.png"
                    alt="hiker"
                    style={{
                      position: 'absolute',
                      width: '16%',
                      height: 'auto',
                      left: `${charXPct}%`,
                      top: `${charYPct}%`,
                      transform: 'translate(-50%, -80%)',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* Progress badge */}
                  <div style={{
                    position: 'absolute', top: 12, left: 12,
                    background: 'rgba(255,255,255,0.92)',
                    borderRadius: 12, padding: '6px 14px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 3 }}><img src="/assets/icons/mountain.png" alt="progress" style={{ width: 12, height: 12, objectFit: 'contain' }} />Progress</span>
                    <span style={{ fontSize: 17, fontWeight: 900, color: '#0D2A4F', lineHeight: 1.2 }}>{progress}%</span>
                  </div>
                </div>

                {/* Bottom strip */}
                <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#6B7280', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><img src={motivationIcon} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />{motivationText}</p>
                  <div style={{ background: '#EEF8F2', color: '#1F9D55', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>✓ On track</div>
                </div>
              </>
            )
          })()}

        </div>
      </div>

      {/* ── 중간 3열 ─────────────────────────────────────────── */}
      <div className="dash-grid-mid">

        {/* Continue Learning */}
        <div style={{ ...card, background: 'linear-gradient(145deg,#4F6BED,#3B52CC)', border: 'none', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,.08)' }} />
          <div style={{ position: 'absolute', bottom: -30, left: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
          <div style={{ position: 'relative' }}>
            <p style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}><img src="/assets/icons/rocket.png" alt="rocket" style={{ width: 22, height: 22, objectFit: 'contain' }} />Continue learning</p>
            <p style={{ margin: 0, fontSize: 15, color: 'rgba(255,255,255,.8)', lineHeight: 1.5 }}>Pick up where you left off and keep going!</p>
          </div>
          <button
            onClick={handleStartLesson}
            style={{ width: '100%', padding: '14px', background: '#fff', color: '#3B52CC', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 16px rgba(0,0,0,.12)', position: 'relative', transition: 'transform .15s', marginTop: 12 }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            Start Lesson! →
          </button>
        </div>

        {/* Daily Streak */}
        <div style={{ ...card, textAlign: 'center' }}>
          <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: '#0D2A4F', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><img src="/assets/icons/streak.png" alt="streak" style={{ width: 18, height: 18, objectFit: 'contain' }} />Daily Streak</p>
          <p style={{ margin: '8px 0 2px', fontSize: 52, fontWeight: 900, color: '#0D2A4F', lineHeight: 1 }}>{dailyStreak}</p>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#7A889B', fontWeight: 600 }}>Day Streak!</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            {DAYS.map((d, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: weekChecks[i] ? '#4F6BED' : '#E9EEF5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                  {weekChecks[i] ? <span style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>✓</span> : null}
                </div>
                <span style={{ fontSize: 11, color: '#7A889B', fontWeight: 600 }}>{d}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Badge Collection */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0D2A4F', display: 'flex', alignItems: 'center', gap: 6 }}><img src="/assets/icons/medal.png" alt="badge" style={{ width: 18, height: 18, objectFit: 'contain' }} />Badge Collection</p>
            <button
              onClick={() => navigate('/badge-collection')}
              style={{ background: 'none', border: 'none', color: '#4F6BED', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
              View All →
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {earnedBadges.slice(0, 3).map((badge) => (
              <div key={badge.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#EEF4FF', border: '2px solid #D4E0FF', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {BADGE_IMAGE[badge.badge_key]
                    ? <img src={BADGE_IMAGE[badge.badge_key]} alt={badge.badge_name} style={{ width: 54, height: 54, objectFit: 'contain' }} />
                    : <img src="/assets/icons/medal.png" alt="badge" style={{ width: 54, height: 54, objectFit: 'contain' }} />
                  }
                </div>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#0D2A4F', textAlign: 'center', lineHeight: 1.3 }}>{badge.badge_name}</p>
              </div>
            ))}
            {earnedBadges.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '16px 0', color: '#7A889B', fontSize: 13 }}>
                Start earning badges!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Improvement + Review Hero 2열 ─────────────────── */}
      <div className="dash-grid-improve">

        {/* 왼쪽: Improvement Through Reattempts */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0D2A4F', display: 'flex', alignItems: 'center', gap: 6 }}><img src="/assets/icons/hint.png" alt="hint" style={{ width: 18, height: 18, objectFit: 'contain' }} />Improvement Through Reattempts</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#E6F9F0,#D1F2E0)', color: '#1F9D55', borderRadius: 999, padding: '6px 12px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
              <span>↗</span><span>+{improvement}% improvement</span>
            </div>
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6B7A90', lineHeight: 1.5 }}>
            Incorrect questions are saved and reattempted, so that accuracy improves over time.
          </p>

          <div className="dash-donut-row">
            {wrongData.map((d, i) => {
              const pct = Number(wrongPercent[i]?.percent || 0)
              const color = d?.[0]?.color || '#ccc'
              const r = 46, stroke = 14, cx = 54, cy = 54
              const circ = 2 * Math.PI * r
              const filled = (pct / 100) * circ
              const patternId = `stripe-${i}`
              return (
                <React.Fragment key={i}>
                  <div
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', cursor: 'default' }}
                  >
                    {hoveredIdx === i && (
                      <div style={{ position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)', background: '#fff', border: '1px solid #E3EAF3', boxShadow: '0 6px 16px rgba(13,42,79,.12)', borderRadius: 10, padding: '8px 12px', minWidth: 130, zIndex: 20 }}>
                        {[0, 1].map((j) => (
                          <div key={j} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, fontWeight: 700, color: '#0D2A4F', marginTop: j ? 4 : 0 }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 999, background: d?.[j]?.color || '#ccc', display: 'inline-block' }} />
                              {d?.[j]?.name}
                            </span>
                            <span>{d?.[j]?.value ?? 0}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ width: 108, height: 108, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="108" height="108" viewBox="0 0 108 108">
                        <defs>
                          <pattern id={patternId} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                            <rect width="6" height="6" fill={color} />
                            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                          </pattern>
                        </defs>
                        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F1F1" strokeWidth={stroke} />
                        <circle
                          cx={cx} cy={cy} r={r} fill="none"
                          stroke={`url(#${patternId})`}
                          strokeWidth={stroke}
                          strokeDasharray={`${filled} ${circ - filled}`}
                          strokeDashoffset={circ * 0.25}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dasharray .6s ease' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <span style={{ fontSize: 18, fontWeight: 800, color: '#0D2A4F' }}>{wrongPercent[i]?.percent || '0'}%</span>
                      </div>
                    </div>
                    <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: i === 3 ? 800 : 700, color: i === 3 ? '#1F9D55' : '#0D2A4F', textAlign: 'center' }}>{chartLabels[i]}</p>
                  </div>
                  {i < wrongData.length - 1 && (
                    <span style={{ color: '#B0BBC8', fontSize: 14, fontWeight: 700, marginTop: -20 }}>›</span>
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* 오른쪽: Review Hero */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: 'linear-gradient(145deg,#FFF8F0,#FFF1E6)', border: '1px solid #FFE0C2' }}>
          <p style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 800, color: '#E8730E', letterSpacing: 1 }}>Review Hero</p>
          <div style={{ width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, position: 'relative' }}>
            <img
              src={`/assets/avatar/review-hero/${(avatar.character || 'explorer').charAt(0).toUpperCase() + (avatar.character || 'explorer').slice(1)}.png`}
              alt="Review Hero"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.12))' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.querySelector('.rh-fallback').style.display = 'flex' }}
            />
            <div className="rh-fallback" style={{ display: 'none', width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#FFD6A8,#FFB366)', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🦸</div>
          </div>
          <p style={{ margin: 0, fontSize: 36, fontWeight: 900, color: '#0D2A4F', lineHeight: 1 }}>
            {wrongData[0]?.[0]?.value || 0}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 700, color: '#7A889B' }}>Questions Corrected</p>
        </div>
      </div>

      {/* ── Learning Path (Level 1~8) ─────────────────────────── */}
      <div style={card}>
        <style>{`
          @keyframes pathPulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(79,107,237,.3); }
            50% { box-shadow: 0 0 0 10px rgba(79,107,237,0); }
          }
          @keyframes pathCheckPop {
            0% { transform: scale(0.6); opacity: 0; }
            60% { transform: scale(1.15); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes pathLineGrow {
            from { width: 0; }
          }
          @keyframes rocketFloat {
            0%, 100% { transform: translateY(0) rotate(-15deg); }
            25% { transform: translateY(-4px) rotate(-10deg); }
            50% { transform: translateY(-2px) rotate(-18deg); }
            75% { transform: translateY(-6px) rotate(-12deg); }
          }
          .lp-node:hover .lp-circle { transform: translateY(-3px); }
        `}</style>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <img src="/assets/icons/map.png" alt="map" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0D2A4F' }}>Your Learning Path</p>
        </div>
        <p style={{ margin: '0 0 32px', fontSize: 13, color: '#7A889B' }}>Track your progress through the levels!</p>

        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {levels.map((lvl, idx) => {
            const done    = lvl < currentLevelNum
            const current = lvl === currentLevelNum
            const locked  = lvl > currentLevelNum
            const isLast  = idx === levels.length - 1
            const progressFraction = Math.max(0, (currentLevelNum - 1) / (totalLevels - 1))
            const segmentStart = idx / (totalLevels - 1)
            const segmentEnd = (idx + 1) / (totalLevels - 1)
            let segFill = 0
            if (progressFraction >= segmentEnd) segFill = 100
            else if (progressFraction > segmentStart) segFill = ((progressFraction - segmentStart) / (segmentEnd - segmentStart)) * 100

            return (
              <div key={lvl} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }} className="lp-node">
                {/* 연결선 (원과 원 사이) */}
                {!isLast && (
                  <div style={{ position: 'absolute', top: 21, left: '50%', right: '-50%', height: 4, zIndex: 0 }}>
                    <div style={{ width: '100%', height: '100%', background: '#E9EEF5', borderRadius: 2 }} />
                    <div style={{
                      position: 'absolute', top: 0, left: 0, height: '100%', borderRadius: 2,
                      background: 'linear-gradient(90deg,#22C55E,#4ADE80)',
                      width: `${segFill}%`,
                      animation: segFill > 0 ? 'pathLineGrow .8s ease-out' : 'none',
                      transition: 'width .6s ease',
                    }} />
                  </div>
                )}

                {/* 현재 레벨 위 로켓 */}
                {current && (
                  <div style={{ position: 'absolute', top: -28, zIndex: 3, animation: 'rocketFloat 3s ease-in-out infinite', lineHeight: 1 }}>
                    <img src="/assets/icons/rocket.png" alt="current" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  </div>
                )}

                {/* 노드 원 */}
                <div className="lp-circle" style={{
                  width: current ? 50 : 42,
                  height: current ? 50 : 42,
                  borderRadius: '50%',
                  background: done
                    ? 'linear-gradient(145deg,#22C55E,#16A34A)'
                    : current
                      ? 'linear-gradient(145deg,#5674F4,#3E5AD7)'
                      : 'linear-gradient(145deg,#F5F7FA,#E8ECF2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: done || current ? '#fff' : '#B0BBC8',
                  position: 'relative', zIndex: 2,
                  boxShadow: done
                    ? '0 4px 12px rgba(34,197,94,.25)'
                    : current
                      ? '0 4px 16px rgba(79,107,237,.3)'
                      : '0 2px 6px rgba(0,0,0,.04)',
                  animation: current ? 'pathPulse 2.5s ease-in-out infinite' : 'none',
                  transition: 'transform .2s ease, box-shadow .2s ease',
                }}>
                  {done && (
                    <svg width="20" height="20" viewBox="0 0 20 20" style={{ animation: 'pathCheckPop .4s ease-out' }}>
                      <path d="M5 10.5l3.5 3.5 6.5-7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  {current && <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>Level {lvl}</span>}
                  {locked && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="5" y="11" width="14" height="10" rx="2" fill="#C5CDD8" />
                      <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="#C5CDD8" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  )}
                </div>

                {/* 라벨 */}
                <span style={{
                  marginTop: 10,
                  fontSize: 11,
                  fontWeight: current ? 800 : done ? 700 : 600,
                  color: current ? '#4F6BED' : done ? '#16A34A' : '#B0BBC8',
                  letterSpacing: current ? 0.3 : 0,
                }}>
                  {current ? 'Current' : `Level ${lvl}`}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── 스타일 헬퍼 ──────────────────────────────────────────────
const card = {
  background: '#ffffff',
  border: '1px solid #E7ECF3',
  borderRadius: 24,
  padding: 24,
  boxShadow: '0 10px 30px rgba(13,42,79,.06)',
}

const statChip = (bg, accent) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: bg,
  border: `1px solid ${accent}33`,
  borderRadius: 16,
  padding: '10px 14px',
  flex: 1,
})

const iconCircle = (bg) => ({
  width: 44, height: 44, borderRadius: 999,
  background: bg, display: 'flex',
  alignItems: 'center', justifyContent: 'center', fontSize: 22,
})

const BADGE_IMAGE = {
  first_lesson:     '/assets/badges/common/first_lesson.png',
  '7day_streak':    '/assets/badges/common/7day_streak.png',
  '100_questions':  '/assets/badges/rare/100_questions.png',
  accuracy_80:      '/assets/badges/rare/accuracy_80.png',
  review_hero:      '/assets/badges/rare/review_hero.png',
  '500_questions':  '/assets/badges/epic/500_questions.png',
  accuracy_90:      '/assets/badges/epic/accuracy_90.png',
  level_1_complete: '/assets/badges/epic/level_1_complete.png',
  level_3_complete: '/assets/badges/epic/level_3_complete.png',
  level_5_complete: '/assets/badges/epic/level_5_complete.png',
  '30day_streak':   '/assets/badges/legendary/30day_streak.png',
  review_master:    '/assets/badges/legendary/review_master.png',
}

export default IndividualDashboardBox
